import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentPostHighSchool,
  StudentPostHighSchoolEditResource,
  StudentPostHighSchoolViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentPostHighSchoolPageBody } from "./StudentPostHighSchoolPageBody";
import { StudentPostHighSchoolPageHeader } from "./StudentPostHighSchoolPageHeader";
import { CollegeContext, collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import produce from "immer";
import { useRouter } from "hooks";

const routeName = "studentPostHighSchool";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
}

export const studentPostHighSchoolPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Post-HS",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentPostHighSchoolPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentPostHighSchool;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();
  
  const [postHighSchool, setPostHighSchool] = useState<StudentPostHighSchool>({} as StudentPostHighSchool);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [postHighSchool] });
  const [viewResource, setViewResource] = useState<StudentPostHighSchoolViewResource>({});
  const [editResource, setEditResource] = useState<StudentPostHighSchoolEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentPostHighSchoolPageRoute.getPath({
        id: id,
        crudMode: crudMode,
        screenType: params.screenType,
      })}`
    );

  const fetch = (id: number) => {
    ApiService.studentPostHighSchools.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
      }

      setPostHighSchool(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentPostHighSchools.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentPostHighSchools.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentPostHighSchoolPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentPostHighSchoolPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      postHighSchool={postHighSchool}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentPostHighSchoolPageBody
      crudMode={params.crudMode}
      postHighSchool={postHighSchool}
      setPostHighSchool={setPostHighSchool}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page isForm header={pageHeader}>
          {pageBody}
        </Page>
      ) : (
        <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="sm"
          header={pageHeader}
          content={pageBody}
        />
      )}

      <CollegeContext.Provider
        value={{
          onCreate: (id) => {
            fetchEditResource();
            ApiService.colleges.get(id).then((response) => {
              setPostHighSchool(
                produce(postHighSchool, (draft) => {
                  draft.collegeId = response.id;
                })
              );
            });
          },
        }}
      >
        {matchTemplatePath(collegePageRoute.templatePath) && <Outlet />}
      </CollegeContext.Provider>
    </>
  );
};
